import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { getStoredConsent } from "@/components/CookieConsent";

type ConsentState = { analytics: boolean; marketing: boolean };
type QueuedEvent = { name: string; props?: Record<string, unknown> };

let initialized = false;
let analyticsEnabled = false;
let marketingEnabled = false;
let posthogInitialized = false;
const queue: QueuedEvent[] = [];

// PostHog project credentials. The `phc_` key is a publishable client-side key
// (same posture as the Supabase anon key), so it's safe to commit.
const POSTHOG_KEY = "phc_shPxHLitFvSJQDp34oaxT2vxfzVSyiDvR4zL85apwBjY";
const POSTHOG_HOST = "https://us.i.posthog.com";

function ensurePostHog() {
  if (posthogInitialized) return;
  if (typeof window === "undefined") return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false, // SPA pageviews handled by AnalyticsRouteTracker
    capture_pageleave: true,
    person_profiles: "identified_only",
    persistence: "localStorage+cookie",
    loaded: (ph) => {
      if (!analyticsEnabled) ph.opt_out_capturing();
    },
  });
  posthogInitialized = true;
}

function readConsent(): ConsentState {
  const stored = getStoredConsent();
  return {
    analytics: Boolean(stored?.categories.analytics),
    marketing: Boolean(stored?.categories.marketing),
  };
}

function enableAnalyticsProviders() {
  // PostHog runs in dev too so we can verify events in the live feed.
  ensurePostHog();
  if (posthogInitialized && posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }
  if (!import.meta.env.PROD) return;
  // Sentry performance + session replay are gated on analytics consent.
  try {
    const client = Sentry.getClient();
    if (!client) return;
    const replay = client.getIntegrationByName?.("Replay") as unknown as
      | { start?: () => void }
      | undefined;
    replay?.start?.();
  } catch {
    // no-op
  }
}

function disableAnalyticsProviders() {
  if (posthogInitialized) {
    try {
      posthog.opt_out_capturing();
      posthog.reset();
    } catch {
      // no-op
    }
  }
  try {
    const client = Sentry.getClient();
    if (!client) return;
    const replay = client.getIntegrationByName?.("Replay") as unknown as
      | { stop?: () => Promise<void> | void }
      | undefined;
    void replay?.stop?.();
  } catch {
    // no-op
  }
}

function flushQueue() {
  if (!analyticsEnabled) return;
  while (queue.length) {
    const evt = queue.shift()!;
    dispatchEvent(evt);
  }
}

function dispatchEvent(evt: QueuedEvent) {
  // Forward to PostHog (product analytics) + Sentry breadcrumbs (debug context).
  if (posthogInitialized) {
    try {
      if (evt.name === "page_view") {
        posthog.capture("$pageview", evt.props);
      } else {
        posthog.capture(evt.name, evt.props);
      }
    } catch {
      // no-op
    }
  }
  try {
    Sentry.addBreadcrumb({
      category: "analytics",
      level: "info",
      message: evt.name,
      data: evt.props,
    });
  } catch {
    // no-op
  }
  if (!import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", evt.name, evt.props ?? {});
  }
}

function applyConsent(state: ConsentState) {
  const prevAnalytics = analyticsEnabled;
  analyticsEnabled = state.analytics;
  marketingEnabled = state.marketing;
  if (analyticsEnabled && !prevAnalytics) {
    enableAnalyticsProviders();
    flushQueue();
  } else if (!analyticsEnabled && prevAnalytics) {
    disableAnalyticsProviders();
  }
}

export function initAnalytics() {
  if (initialized) return;
  initialized = true;
  applyConsent(readConsent());
  window.addEventListener("emrkt:cookie-consent", (e) => {
    const detail = (e as CustomEvent).detail?.categories as
      | { analytics: boolean; marketing: boolean }
      | undefined;
    if (detail) applyConsent({ analytics: detail.analytics, marketing: detail.marketing });
  });
}

export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (!analyticsEnabled) {
    // Drop silently when the user has not consented; keep a tiny queue only
    // for events fired immediately on load (max 20).
    if (queue.length < 20) queue.push({ name, props });
    return;
  }
  dispatchEvent({ name, props });
}

export function trackPageView(path: string) {
  trackEvent("page_view", { path });
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!analyticsEnabled || !posthogInitialized) return;
  try {
    posthog.identify(userId, traits);
  } catch {
    // no-op
  }
}

export function resetUser() {
  if (!posthogInitialized) return;
  try {
    posthog.reset();
  } catch {
    // no-op
  }
}

export function hasAnalyticsConsent() {
  return analyticsEnabled;
}

export function hasMarketingConsent() {
  return marketingEnabled;
}